import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { ChevronRight, Coins, Gift, KeyRound, Loader2 } from 'lucide-react'
import { getCatalog, type CatalogPack } from '@/api/billing'
import { quickSetupLlm, testProviderKey } from '@/api/settings'
import { useRazorpayCheckout } from '@/hooks/useRazorpayCheckout'
import { refreshCredits } from '@/store/creditsStore'
import TopUpPacks from '@/pages/billing/TopUpPacks'
import type { AiAccessError } from '@/store/aiAccessStore'

type Section = 'buy' | 'key' | 'free' | null

const SPIN = { animation: 'spin 1s linear infinite' } as const
type OwnKeyProvider = 'anthropic' | 'openai'

function apiMessage(e: unknown, fallback: string): string {
  const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return typeof d === 'string' && d ? d : fallback
}

/**
 * The three ways to keep building when the AI credits run out, offered as one
 * choice everywhere a refusal can surface (HTTP 402 and the agent sockets):
 *
 *  1. **Buy credits** — the pack grid and gateway checkout, inline.
 *  2. **Use my own AI key** — an Anthropic or OpenAI key becomes the user's
 *     personal AI configuration (never charged credits).
 *  3. **Continue with a free model** — a no-cost OpenCode model.
 *
 * Which of them is offered comes from the server (`error.options`), so a card
 * never offers something this instance cannot do. Each ends in `onContinue`,
 * which carries the refused work on (replay or resend — see AiAccessModal).
 */
export default function OutOfCreditsOptions({
  error, onContinue, onCancel,
}: {
  error: AiAccessError
  onContinue: (message: string, aiChanged: boolean) => void
  onCancel: () => void
}) {
  const navigate = useNavigate()
  const opts = error.options || {}
  // Buying credits never helps a monthly allowance (billing off) — the
  // server says so with packs_available=false.
  const canBuy = !!opts.packs_available
  const paymentsReady = !!opts.payments_ready
  const freeModel = opts.free_model || null

  const [open, setOpen] = useState<Section>(canBuy ? 'buy' : null)
  const [packs, setPacks] = useState<CatalogPack[] | null>(null)
  const [packsError, setPacksError] = useState<string | null>(null)
  const { checkout, busy: paying } = useRazorpayCheckout()

  const [provider, setProvider] = useState<OwnKeyProvider>('anthropic')
  const [apiKey, setApiKey] = useState('')
  const [working, setWorking] = useState(false)

  const loadPacks = useCallback(async () => {
    if (packs !== null) return
    try {
      const cat = await getCatalog()
      setPacks(cat.packs || [])
    } catch {
      setPacksError('Could not load the credit packs. Try again from Billing & credits.')
      setPacks([])
    }
  }, [packs])

  const toggle = (s: Exclude<Section, null>) => {
    setOpen((cur) => (cur === s ? null : s))
  }
  // The buy section opens itself when offered; load its packs straight away.
  useEffect(() => {
    if (open === 'buy') void loadPacks()
  }, [open, loadPacks])

  const buyPack = useCallback(async (packCode: string, couponCode?: string) => {
    const result = await checkout({
      purpose: 'credit_pack', target_ref: packCode, coupon_code: couponCode,
      description: 'Twasta.ai credits',
    })
    if (result.cancelled) return
    if (!result.ok) {
      toast.error(result.error || 'The payment did not complete')
      return
    }
    refreshCredits()
    onContinue('Credits added — carrying on…', false)
  }, [checkout, onContinue])

  const applyOwnKey = async () => {
    const key = apiKey.trim()
    if (!key) { toast.error('Paste your API key'); return }
    setWorking(true)
    try {
      const test = await testProviderKey(provider, key)
      if (!test.ok) {
        toast.error(test.error ? `That key didn't work: ${test.error}` : "That key didn't work")
        return
      }
      await quickSetupLlm({ kind: 'own_key', provider, api_key: key })
      onContinue('Your key is set up — carrying on…', true)
    } catch (e) {
      toast.error(apiMessage(e, 'Could not save your key'))
    } finally {
      setWorking(false)
    }
  }

  const applyFreeModel = async () => {
    setWorking(true)
    try {
      await quickSetupLlm({ kind: 'free_model', model: freeModel?.id })
      onContinue('Switched to a free model — carrying on…', true)
    } catch (e) {
      toast.error(apiMessage(e, 'Could not switch to the free model'))
    } finally {
      setWorking(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {canBuy && (
        <Choice
          icon={<Coins size={18} />} title="Buy credits"
          subtitle={paymentsReady ? 'Top up and carry straight on.' : 'Payments aren’t set up on this server yet.'}
          open={open === 'buy'} onToggle={() => toggle('buy')} disabled={!paymentsReady}
        >
          {packs === null ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
              <Loader2 size={14} style={SPIN} /> Loading credit packs…
            </div>
          ) : packsError ? (
            <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>{packsError}</p>
          ) : (
            <TopUpPacks packs={packs} busy={paying} onBuy={buyPack} heading="Choose a pack" />
          )}
        </Choice>
      )}

      <Choice
        icon={<KeyRound size={18} />} title="Use my own AI key"
        subtitle="Your Anthropic or OpenAI key — usage on it is never charged credits."
        open={open === 'key'} onToggle={() => toggle('key')}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
          <label style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
            Provider
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value as OwnKeyProvider)}
              style={{ marginTop: '0.25rem', width: '100%' }}
            >
              <option value="anthropic">Anthropic (Claude)</option>
              <option value="openai">OpenAI</option>
            </select>
          </label>
          <label style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
            API key
            <input
              type="password" value={apiKey} autoFocus
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={provider === 'anthropic' ? 'sk-ant-…' : 'sk-…'}
              style={{ marginTop: '0.25rem', width: '100%' }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !working) void applyOwnKey() }}
            />
          </label>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
            <button
              style={{
                padding: 0, fontSize: '0.8125rem', background: 'none', border: 'none',
                color: 'var(--color-accent)', cursor: 'pointer', textAlign: 'left',
              }}
              onClick={() => { onCancel(); navigate('/settings') }}
            >
              Another provider or a subscription? Settings → LLM
            </button>
            <button className="btn btn-primary" onClick={() => void applyOwnKey()} disabled={working}>
              {working ? <><Loader2 size={14} style={SPIN} /> Checking…</> : 'Test & continue'}
            </button>
          </div>
          <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: 0 }}>
            Stored encrypted and used only for your account. You can switch back
            to Twasta’s AI any time in Settings → LLM.
          </p>
        </div>
      </Choice>

      {freeModel && (
        <Choice
          icon={<Gift size={18} />} title="Continue with a free model"
          subtitle={`${freeModel.label} — no cost, but slower and less capable.`}
          open={open === 'free'} onToggle={() => toggle('free')}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
            <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', margin: 0, lineHeight: 1.5 }}>
              Your work carries on with <strong>{freeModel.label}</strong> at no charge.
              Results may be simpler than with Twasta’s AI — buy credits or add your
              own key whenever you want to switch back.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" onClick={() => void applyFreeModel()} disabled={working}>
                {working ? <><Loader2 size={14} style={SPIN} /> Switching…</> : 'Switch to the free model'}
              </button>
            </div>
          </div>
        </Choice>
      )}

      <button className="btn btn-secondary" onClick={onCancel} style={{ marginTop: '0.25rem' }}>
        Not now
      </button>
    </div>
  )
}

function Choice({
  icon, title, subtitle, open, onToggle, disabled = false, children,
}: {
  icon: ReactNode
  title: string
  subtitle: string
  open: boolean
  onToggle: () => void
  disabled?: boolean
  children: ReactNode
}) {
  return (
    <div style={{
      border: `1px solid ${open ? 'var(--color-accent)' : 'var(--color-border)'}`,
      borderRadius: 'var(--radius-md)', background: 'var(--color-bg-secondary)',
      opacity: disabled ? 0.65 : 1,
    }}>
      <button
        onClick={onToggle} disabled={disabled} aria-expanded={open}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%',
          padding: '0.75rem 0.875rem', background: 'none', border: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer', textAlign: 'left',
          color: 'var(--color-text)',
        }}
      >
        <span style={{ color: 'var(--color-accent)', display: 'flex' }}>{icon}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontWeight: 600, fontSize: '0.9375rem' }}>{title}</span>
          <span style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: '0.125rem' }}>
            {subtitle}
          </span>
        </span>
        <ChevronRight size={16} style={{
          transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s',
          color: 'var(--color-text-secondary)',
        }} />
      </button>
      {open && !disabled && (
        <div style={{ padding: '0 0.875rem 0.875rem' }}>{children}</div>
      )}
    </div>
  )
}
